/**
 * De kaartbrowser: een stapel doorkijken en er een kaart uit kiezen.
 *
 * Dezelfde carrousel als de kaartpockets, maar dan links onderin en met de inhoud van een
 * uitbouwstapel (zie `catan-ref/shots/ref/30-stapelbrowser.png` en `b02.png`). Je ziet
 * vier kaarten tegelijk met hun naam eronder, en een bladerbalk om verder te kijken.
 *
 * Anders dan bij de pockets is bladeren hier wél nodig: een stapel telt er elf tot
 * dertien.
 */
import { Container, Sprite, Texture } from 'pixi.js';
import { asset, hasAsset, klik } from './assets.js';
import { KOSTEN_ICOON, kostenRijen } from './kosten.js';
import { negenStuk, tekst, SKIN } from './skin.js';

/** Hoeveel kaarten er tegelijk in beeld staan, zoals in het origineel. */
const ZICHTBAAR = 4;

export interface BrowserKaart {
  readonly id: number;
  readonly naam: string;
  /**
   * Uit welke plaatgroep het beeld komt; standaard de uitbouwkaarten.
   *
   * De Verkenner laat je door de **landschap**stapel bladeren, en die platen zitten in
   * een andere groep. Verder is het dezelfde carrousel.
   */
  readonly groep?: string;
  /** De sleutel binnen die groep; standaard het kaartnummer. */
  readonly plaat?: string;
  /**
   * Wat de kaart kost, voor de kostenstrook linksboven.
   *
   * Juist hier onmisbaar: in deze browser kies je een kaart die je zo dadelijk gaat
   * betalen — zonder strook kies je blind. De Verkenner bladert door landschappen en
   * laat het veld gewoon weg.
   */
  readonly kosten?: Readonly<Record<string, number | undefined>>;
}

export interface BrowserOpties {
  readonly kaarten: readonly BrowserKaart[];
  readonly titel: string;
  /** De aanroeper krijgt de plaats in de stapel terug, niet het kaartnummer. */
  readonly onKies: (plek: number) => void;
  readonly onSluiten: () => void;
}

/** Waar de browser staat, opgemeten op de schermafdrukken van het origineel. */
const PLEK = { x: 40, y: 368, cel: 100, kier: 4, plaatHoog: 68, naamHoog: 20, marge: 10 } as const;

export function kaartBrowser(opties: BrowserOpties): Container {
  const wortel = new Container();
  wortel.x = PLEK.x;
  wortel.y = PLEK.y;

  let vanaf = 0;
  const laag = new Container();
  wortel.addChild(laag);

  const breed = ZICHTBAAR * (PLEK.cel + PLEK.kier) - PLEK.kier + PLEK.marge * 2;
  const balkHoog = 20;
  const hoog = PLEK.plaatHoog + PLEK.naamHoog + PLEK.marge * 2 + balkHoog;

  function teken(): void {
    laag.removeChildren();
    laag.addChild(negenStuk(SKIN.paneelGroot, breed, hoog, 14));

    const kop = tekst(opties.titel, 13);
    kop.anchor.set(0.5, 1);
    kop.x = breed / 2;
    kop.y = -4;
    laag.addChild(kop);

    const venster = opties.kaarten.slice(vanaf, vanaf + ZICHTBAAR);
    for (const [i, kaart] of venster.entries()) {
      const vak = new Container();
      vak.x = PLEK.marge + i * (PLEK.cel + PLEK.kier);
      vak.y = PLEK.marge;

      const groep = kaart.groep ?? 'kaarten';
      const sleutel = kaart.plaat ?? String(kaart.id);
      if (hasAsset(groep, sleutel)) {
        const plaat = new Sprite(Texture.from(asset(groep, sleutel).src));
        plaat.setSize(PLEK.cel, PLEK.plaatHoog);
        vak.addChild(plaat);
      }

      // Dezelfde kostenstrook als op de handkaart: rij per soort, aantal naast elkaar.
      for (const [rij, { soort, aantal }] of kostenRijen(kaart.kosten).entries()) {
        if (!hasAsset('symbolen', soort)) continue;
        for (let k = 0; k < aantal; k++) {
          const icoon = new Sprite(Texture.from(asset('symbolen', soort).src));
          icoon.setSize(KOSTEN_ICOON.maat, KOSTEN_ICOON.maat);
          icoon.x = 2 + k * KOSTEN_ICOON.stap;
          icoon.y = 2 + rij * KOSTEN_ICOON.stap;
          vak.addChild(icoon);
        }
      }

      const plaquette = negenStuk(SKIN.plaquette, PLEK.cel, PLEK.naamHoog, 6);
      plaquette.y = PLEK.plaatHoog;
      vak.addChild(plaquette);

      const naam = tekst(kaart.naam, 11);
      naam.anchor.set(0.5, 0.5);
      naam.x = PLEK.cel / 2;
      naam.y = PLEK.plaatHoog + PLEK.naamHoog / 2;
      vak.addChild(naam);

      vak.eventMode = 'static';
      vak.cursor = 'pointer';
      const plek = vanaf + i;
      vak.on('pointertap', () => {
        klik();
        opties.onKies(plek);
      });
      vak.on('pointerover', () => (vak.y = PLEK.marge - 6));
      vak.on('pointerout', () => (vak.y = PLEK.marge));
      laag.addChild(vak);
    }

    // De bladerbalk met de twee pijlen; die doen hier wél iets.
    const balk = new Container();
    balk.x = PLEK.marge;
    balk.y = hoog - balkHoog - 4;
    balk.addChild(negenStuk(SKIN.paneelSmal, breed - PLEK.marge * 2, balkHoog, 6));

    const pijl = (naam: string, x: number, stap: number, kan: boolean): void => {
      if (!hasAsset('skin', naam)) return;
      const beeld = new Sprite(Texture.from(asset('skin', naam).src));
      beeld.setSize(44, 17);
      beeld.x = x;
      beeld.y = (balkHoog - 17) / 2;
      beeld.alpha = kan ? 0.85 : 0.3;
      if (kan) {
        beeld.eventMode = 'static';
        beeld.cursor = 'pointer';
        beeld.on('pointertap', () => {
          klik();
          vanaf = Math.max(0, Math.min(opties.kaarten.length - ZICHTBAAR, vanaf + stap));
          teken();
        });
      }
      balk.addChild(beeld);
    };
    pijl(SKIN.pijlLinks, 4, -ZICHTBAAR, vanaf > 0);
    pijl(
      SKIN.pijlRechts,
      breed - PLEK.marge * 2 - 48,
      ZICHTBAAR,
      vanaf + ZICHTBAAR < opties.kaarten.length,
    );
    laag.addChild(balk);

    // Een sluitknop rechts naast de balk, want je hoeft niet te kiezen.
    const dicht = tekst('✕', 15);
    dicht.x = breed - 18;
    dicht.y = 4;
    dicht.eventMode = 'static';
    dicht.cursor = 'pointer';
    dicht.on('pointertap', () => {
      klik();
      opties.onSluiten();
    });
    laag.addChild(dicht);
  }

  teken();
  return wortel;
}
